import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  FolderKanban, 
  FileSpreadsheet, 
  TrendingUp, 
  Landmark,
  Calculator,
  Settings,
  LogOut,
  Menu,
  X,
  Globe,
  User,
  ChevronDown
} from 'lucide-react';

const Layout = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout, updateLanguage } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/projects', icon: FolderKanban, label: t('nav.projects') },
    { path: '/balance-sheet', icon: FileSpreadsheet, label: t('nav.balanceSheet') },
    { path: '/business-plan', icon: TrendingUp, label: t('nav.businessPlan') },
    { path: '/project-financing', icon: Landmark, label: t('nav.projectFinancing') },
    { path: '/valuation', icon: Calculator, label: t('nav.valuation') },
    { path: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleLanguageChange = (lang) => {
    updateLanguage(lang);
  };

  // Get user avatar - either profile image or initials
  const getUserAvatar = () => {
    if (user?.profile_image) {
      return (
        <img 
          src={user.profile_image} 
          alt={user.full_name}
          className="w-full h-full rounded-full object-cover"
        />
      );
    }
    return <User size={20} className="text-primary" />;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="flex items-center justify-between px-3 sm:px-4 h-14 sm:h-16">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-muted rounded-lg"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <Link to="/dashboard" className="font-bold text-lg sm:text-xl gradient-text">
            AI FM DEMO
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full w-9 h-9 sm:w-10 sm:h-10" data-testid="user-menu-mobile">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                  {getUserAvatar()}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }}>
                <Settings className="mr-2 h-4 w-4" /> {t('nav.settings')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleLanguageChange('it')}>
                <Globe className="mr-2 h-4 w-4" /> Italiano
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleLanguageChange('en')}>
                <Globe className="mr-2 h-4 w-4" /> English
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" /> {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 z-40 h-screen bg-card border-r border-border/50
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'w-64' : 'w-20'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-14 sm:h-16 flex items-center justify-between px-4 border-b border-border/50">
          {sidebarOpen && (
            <Link to="/dashboard" className="font-bold text-lg gradient-text truncate" data-testid="logo-link">
              AI FM DEMO
            </Link>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
            data-testid="sidebar-toggle"
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 sm:p-4 space-y-1 sm:space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl transition-all duration-200
                  ${isActive 
                    ? 'bg-primary text-primary-foreground shadow-md' 
                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  }
                `}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon size={20} className="flex-shrink-0" />
                {sidebarOpen && <span className="font-medium truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 border-t border-border/50">
          {sidebarOpen ? (
            <Link to="/settings" className="flex items-center gap-3 hover:bg-muted p-2 rounded-xl transition-colors">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                {getUserAvatar()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate text-sm sm:text-base">{user?.full_name}</p>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">{user?.email}</p>
              </div>
            </Link>
          ) : (
            <Link to="/settings" className="flex justify-center">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                {getUserAvatar()}
              </div>
            </Link>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Desktop header */}
        <header 
          className="hidden lg:flex fixed top-0 right-0 left-0 z-30 glass border-b border-border/50 h-16 items-center justify-between px-4 xl:px-6" 
          style={{ marginLeft: sidebarOpen ? '256px' : '80px' }}
        >
          <div className="flex items-center gap-4">
            <h1 className="text-base xl:text-lg font-semibold text-foreground truncate">
              {navItems.find(item => location.pathname.startsWith(item.path))?.label || t('nav.dashboard')}
            </h1>
          </div>
          <div className="flex items-center gap-2 xl:gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" data-testid="language-selector">
                  <Globe size={16} />
                  <span className="hidden sm:inline">{user?.language === 'en' ? 'EN' : 'IT'}</span>
                  <ChevronDown size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleLanguageChange('it')} data-testid="lang-it">
                  Italiano
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleLanguageChange('en')} data-testid="lang-en">
                  English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2 xl:px-4" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                    {getUserAvatar()}
                  </div>
                  <span className="font-medium hidden xl:inline truncate max-w-32">{user?.full_name}</span>
                  <ChevronDown size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                  <Settings className="mr-2 h-4 w-4" />
                  {t('nav.settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="pt-16 sm:pt-18 lg:pt-20 p-4 sm:p-6 lg:p-8 min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
